import { FC, ReactElement, useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  TextField,
  FormControl,
  Link,
  Button,
  Divider,
  useTheme,
  useMediaQuery,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Switch,
} from '@mui/material';
// Removed unused useTranslation import; language selection now via LanguageMenu
import LanguageMenu from 'components/LanguageMenu';
import { useTranslation } from 'react-i18next';
// Switch now imported above
import LanguageAvatar from 'components/LanguageAvatar';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
// import LanguageIcon from '@mui/icons-material/Language'; // replaced by LanguageAvatar
import { useActor } from 'contexts/ActorContext';
import { writeActorAugment, writeActorRelease, fetchActorArchive } from 'api';
import { TOS, PRIVACY, LOGIN } from 'routes';

/**
 * Actor Account page: view and edit profile, preferences, and manage account.
 */
const ActorAccount: FC = (): ReactElement | null => {
  const { actor, setActor } = useActor();
  const location = useLocation();
  // Extract scrollTo from navigation state
  const state = location.state as { scrollTo?: string } | undefined;
  const navigate = useNavigate();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const { t, i18n } = useTranslation('actorAccount');
  // Set document title to account header
  useEffect(() => {
    document.title = `${t('title')} - ${t('account')}`;
  }, [t]);
  // Display label for active language
  // Determine active language code (fallback to i18n or 'en')
  const activeLang = actor?.actorLingo || i18n.language.split('-')[0] || 'en';
  const displayLabel = useMemo(() => {
    const disp = new Intl.DisplayNames([i18n.language], { type: 'language' });
    const name = disp.of(activeLang) || activeLang;
    return name.charAt(0).toLocaleUpperCase(i18n.language) + name.slice(1);
  }, [activeLang, i18n.language]);
  // Local name fields
  const [firstName, setFirstName] = useState<string>('');
  const [lastName, setLastName] = useState<string>('');
  // State for download in progress
  const [isDownloading, setIsDownloading] = useState<boolean>(false);
  useEffect(() => {
    if (actor) {
      setFirstName(actor.actorGname || '');
      setLastName(actor.actorLname || '');
    }
  }, [actor]);

  // Refs for scrolling to sections
  const profileRef = useRef<HTMLDivElement>(null);
  const preferencesRef = useRef<HTMLDivElement>(null);
  const dataPrivacyRef = useRef<HTMLDivElement>(null);
  const getCopyRef = useRef<HTMLDivElement>(null);
  const personalizationRef = useRef<HTMLDivElement>(null);
  const accountManagementRef = useRef<HTMLDivElement>(null);
  const scrollTo = state?.scrollTo;
  const [expanded, setExpanded] = useState<string | false>(false);
  const handleChange = (panel: string) => (_event: React.SyntheticEvent, isExpanded: boolean) => {
    setExpanded(isExpanded ? panel : false);
  };
  // Open and scroll to specified section when navigating via menu
  useEffect(() => {
    if (scrollTo) {
      setExpanded(scrollTo);
      const refs: Record<string, React.RefObject<HTMLDivElement>> = {
        profileInformation: profileRef,
        accountPreferences: preferencesRef,
        dataPrivacy: dataPrivacyRef,
        getCopyOfYourData: getCopyRef,
        personalization: personalizationRef,
        accountManagement: accountManagementRef,
      };
      const ref = refs[scrollTo];
      if (ref && ref.current) {
        ref.current.scrollIntoView({ behavior: 'smooth' });
      }
    }
  }, [scrollTo]);
  if (!actor) {
    return null;
  }

  // Handle blur updates for first name: validate non-empty, otherwise restore original
  const handleFirstBlur = async () => {
    const prevName = actor.actorGname || '';
    const trimmed = firstName.trim();
    // If cleared or all whitespace, revert to original
    if (!trimmed) {
      setFirstName(prevName);
      return;
    }
    // If name changed, attempt update
    if (trimmed !== prevName) {
      // Optimistically set trimmed value
      setFirstName(trimmed);
      try {
        const resp = await writeActorAugment({ actorGName: trimmed });
        if (resp.actorMetadata) {
          setActor(resp.actorMetadata);
          const newName = resp.actorMetadata.actorGname || prevName;
          setFirstName(newName);
        } else {
          // No actor returned: revert
          setFirstName(prevName);
        }
      } catch (err) {
        console.error('Update first name failed', err);
        // On error, revert to original
        setFirstName(prevName);
      }
    }
  };
  // Handle blur updates for last name: validate non-empty, otherwise restore original
  const handleLastBlur = async () => {
    const prevName = actor.actorLname || '';
    const trimmed = lastName.trim();
    // If cleared or all whitespace, revert to original
    if (!trimmed) {
      setLastName(prevName);
      return;
    }
    // If name changed, attempt update
    if (trimmed !== prevName) {
      // Optimistically set trimmed value
      setLastName(trimmed);
      try {
        const resp = await writeActorAugment({ actorLName: trimmed });
        if (resp.actorMetadata) {
          setActor(resp.actorMetadata);
          const newName = resp.actorMetadata.actorLname || prevName;
          setLastName(newName);
        } else {
          // No actor returned: revert
          setLastName(prevName);
        }
      } catch (err) {
        console.error('Update last name failed', err);
        // On error, revert to original
        setLastName(prevName);
      }
    }
  };

  // Download user data
  const handleDownload = async () => {
    setIsDownloading(true);
    try {
      const data = await fetchActorArchive();
      const json = JSON.stringify(data, null, 2);
      const blob = new Blob([json], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      // Generate filename with current date: title-archive-YYYY-MM-DD.json
      const now = new Date();
      const year = now.getFullYear();
      const month = String(now.getMonth() + 1).padStart(2, '0');
      const day = String(now.getDate()).padStart(2, '0');
      link.download = `title-archive-${year}-${month}-${day}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Failed to download user data', err);
    } finally {
      setIsDownloading(false);
    }
  };

  // Close account
  const handleClose = async () => {
    if (window.confirm(t('confirmDeleteAccount'))) {
      try {
        await writeActorRelease();
      } catch (e) {
        console.error('Account deletion failed', e);
      }
      setActor(undefined);
      navigate(LOGIN);
    }
  };

  return (
    <Container maxWidth="xl" disableGutters={isSmallScreen}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, px: { xs: 1, sm: 2 }, pt: 4, pb: 3 }}>
        <Typography variant="h3" component="h2">{t('account')}</Typography>
      </Box>
      {/* Profile information */}
      <Accordion
        expanded={expanded === 'profileInformation'}
        onChange={handleChange('profileInformation')}
        ref={profileRef}
        sx={(theme) => ({
          scrollMarginTop: theme.mixins.toolbar.minHeight,
        })}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          sx={{ px: { xs: 1, sm: 2 }, py: 1 }}
        >
          <Typography variant="h6">{t('profileInformation')}</Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ px: { xs: 1, sm: 2 }, py: 3 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            * {t('indicatesRequired')}
          </Typography>
          <FormControl fullWidth sx={{ mb: 2 }}>
            <TextField
              label={t('firstName')}
              value={firstName}
              placeholder={actor.actorGname || ''}
              required
              onChange={e => setFirstName(e.target.value)}
              onBlur={handleFirstBlur}
            />
          </FormControl>
          <FormControl fullWidth sx={{ mb: 2 }}>
            <TextField
              label={t('lastName')}
              value={lastName}
              placeholder={actor.actorLname || ''}
              required
              onChange={e => setLastName(e.target.value)}
              onBlur={handleLastBlur}
            />
          </FormControl>
          <FormControl fullWidth>
            <TextField
              label={t('email')}
              value={actor.actorEmail}
              required
              InputProps={{
                readOnly: true,
                inputProps: { style: { color: theme.palette.text.disabled } },
              }}
            />
          </FormControl>
        </AccordionDetails>
      </Accordion>
      <Divider />
      {/* Account preferences */}
      <Accordion
        expanded={expanded === 'accountPreferences'}
        onChange={handleChange('accountPreferences')}
        ref={preferencesRef}
        sx={(theme) => ({
          scrollMarginTop: theme.mixins.toolbar.minHeight,
        })}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          sx={{ px: { xs: 1, sm: 2 }, py: 1 }}
        >
          <Typography variant="h6">{t('accountPreferences')}</Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ px: { xs: 1, sm: 2 }, py: 3 }}>
          <Box sx={{ mt: 2, mb: 1 }}>
            <LanguageMenu
              renderTrigger={({ onClick, ref, sx }) => (
                <Box
                  ref={ref}
                  onClick={onClick}
                  sx={{
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                    px: 2,
                    py: 1,
                    display: 'flex',
                    alignItems: 'center',
                    width: '100%',
                    cursor: 'pointer',
                    ...sx as object,
                  }}
                >
                  <Typography variant="body1">{displayLabel}</Typography>
                  <LanguageAvatar language={activeLang} sx={{ ml: 'auto' }} />
                </Box>
              )}
            />
          </Box>
          <Typography variant="body2" color="text.secondary">
            {t('preferencesDescription')}
          </Typography>
        </AccordionDetails>
      </Accordion>
      <Divider />
      {/* Data privacy */}
      <Accordion
        expanded={expanded === 'dataPrivacy'}
        onChange={handleChange('dataPrivacy')}
        ref={dataPrivacyRef}
        sx={(theme) => ({
          scrollMarginTop: theme.mixins.toolbar.minHeight,
        })}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          sx={{ px: { xs: 1, sm: 2 }, py: 1 }}
        >
          <Typography variant="h6">{t('dataPrivacy')}</Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ px: { xs: 1, sm: 2 }, py: 3 }}>
          <Box sx={{ mt: 1, mb: 1 }}>
            <Link underline="hover" href={PRIVACY} sx={{ mr: 2 }}>
              {t('privacyPolicy')}
            </Link>
            <Link underline="hover" href={TOS}>
              {t('termsOfService')}
            </Link>
          </Box>
        </AccordionDetails>
      </Accordion>
      <Divider />
      {/* Get a copy of your data */}
      <Accordion
        expanded={expanded === 'getCopyOfYourData'}
        onChange={handleChange('getCopyOfYourData')}
        ref={getCopyRef}
        sx={(theme) => ({
          scrollMarginTop: theme.mixins.toolbar.minHeight,
        })}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          sx={{ px: { xs: 1, sm: 2 }, py: 1 }}
        >
          <Typography variant="h6">{t('getCopyOfYourData')}</Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ px: { xs: 1, sm: 2 }, py: 3 }}>
          <Button
            variant="contained"
            sx={{ mt: 1 }}
            onClick={handleDownload}
            disabled={isDownloading}
          >
            {t('downloadYourData')}
          </Button>
        </AccordionDetails>
      </Accordion>
      <Divider />
      {/* Personalization */}
      <Accordion
        expanded={expanded === 'personalization'}
        onChange={handleChange('personalization')}
        ref={personalizationRef}
        sx={(theme) => ({
          scrollMarginTop: theme.mixins.toolbar.minHeight,
        })}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          sx={{ px: { xs: 1, sm: 2 }, py: 1 }}
        >
          <Typography variant="h6">{t('personalization')}</Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ px: { xs: 1, sm: 2 }, py: 3 }}>
          <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column' }}>
            <Typography variant="body1">{t('disablePersonalization')}</Typography>
            <Switch
              checked={actor.limitUsage}
              onChange={async (_e, checked) => {
                const prevActor = actor;
                setActor({ ...actor, limitUsage: checked });
                try {
                  const resp = await writeActorAugment({ limitUsage: checked });
                  if (resp.actorMetadata && resp.actorMetadata.limitUsage === checked) {
                    setActor(resp.actorMetadata);
                  } else {
                    setActor(prevActor);
                  }
                } catch (err) {
                  console.error('Failed to update limitUsage', err);
                  setActor(prevActor);
                }
              }}
            />
          </Box>
        </AccordionDetails>
      </Accordion>
      <Divider />
      {/* Account management */}
      <Accordion
        expanded={expanded === 'accountManagement'}
        onChange={handleChange('accountManagement')}
        ref={accountManagementRef}
        sx={(theme) => ({
          scrollMarginTop: theme.mixins.toolbar.minHeight,
        })}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          sx={{ px: { xs: 1, sm: 2 }, py: 1 }}
        >
          <Typography variant="h6">{t('accountManagement')}</Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ px: { xs: 1, sm: 2 }, py: 3 }}>
          <Button variant="text" onClick={handleClose}>
            {t('closeAccount')}
          </Button>
        </AccordionDetails>
      </Accordion>
    </Container>
  );
};

export default ActorAccount;